import React from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';

const getTransactionTotals = (transactions = []) => {
  return transactions.reduce(
    (acc, transaction) => {
      const numericAmount = parseFloat(transaction.amount) || 0;
      if (transaction._type === 'income') {
        acc.total += numericAmount;
      } else {
        acc.total -= numericAmount;
      }
      return acc;
    },
    { total: 0 }
  );
};

const getTransactionDateParts = (dateString) => {
  if (!dateString) {
    return { date: 'Data non disponibile', time: '--:--' };
  }

  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) {
    return { date: 'Data non disponibile', time: '--:--' };
  }

  return {
    date: format(parsedDate, 'dd MMM yyyy', { locale: it }),
    time: format(parsedDate, 'HH:mm', { locale: it })
  };
};

function StoreTransactionsPanel({ transactions = [], onSelectTransaction }) {
  const totalTransactions = transactions.length;
  const { total } = getTransactionTotals(transactions);
  const averageAmount = totalTransactions > 0 ? total / totalTransactions : 0;
  const averageDisplayValue = Math.abs(averageAmount);
  const averageColorClass = averageAmount > 0
    ? 'text-green-700 dark:text-green-300'
    : averageAmount < 0
      ? 'text-red-700 dark:text-red-300'
      : 'text-gray-900 dark:text-gray-100';

  return (
    <div className="mt-3 w-full lg:max-w-none">
      <div className="px-4 py-4 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white/80 dark:bg-gray-900/60 shadow-sm space-y-3">
        <div className="flex items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="uppercase tracking-wide text-[11px] font-semibold text-gray-500 dark:text-gray-400">
              Numero transazioni
            </span>
            <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {totalTransactions}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-wide text-[11px] font-semibold text-gray-500 dark:text-gray-400">
              Importo medio
            </span>
            <span className={`text-xl font-semibold ${averageColorClass}`}>
              {formatCurrency(averageDisplayValue)}
            </span>
          </div>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
            Nessuna transazione disponibile per questo negozio
          </div>
        ) : (
          <div className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1">
            {transactions.map((transaction) => {
              const isExpense = transaction._type === 'expense';
              const amount = parseFloat(transaction.amount) || 0;
              const signedAmount = isExpense ? -amount : amount;
              const { date, time } = getTransactionDateParts(transaction.date);
              const displayDate = time && time !== '--:--' ? `${date} • ${time}` : date;
              const amountPrefix = isExpense ? '-' : '+';
              const amountColorClass = isExpense
                ? 'text-red-700 dark:text-red-300'
                : 'text-green-700 dark:text-green-300';

              return (
                <button
                  type="button"
                  key={transaction.id}
                  className="w-full text-left border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/70 rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onClick={() => onSelectTransaction && onSelectTransaction(transaction)}
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-gray-600 dark:text-gray-300 truncate">
                      {displayDate}
                    </span>
                    <span className={`font-semibold ${amountColorClass}`}>
                      {`${amountPrefix}${formatCurrency(Math.abs(signedAmount))}`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreTransactionsPanel;
